'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import type { SignedIn } from '@pubky/passport-client';
import { APP_ROUTES, ONBOARDING_ROUTES } from '@/app/routes';
import { AuthController } from '@/controllers/auth/auth';
import { isAppError, isWrongEnvironmentHomeserverError } from '@/libs/error/error.utils';
import { toast } from '@/molecules/Toaster/toast';
import { useAuthStore } from '@/stores/auth/auth.store';
import { useOnboardingStore } from '@/stores/onboarding/onboarding.store';

/** Only the real SDK Session enters Pubky; all auth UI and profile setup belongs to Passport. */
export function usePassportSession() {
  const router = useRouter();
  const [isInitializing, setIsInitializing] = useState(false);
  const acceptSession = async ({ session, publicKey, instance }: SignedIn) => {
    setIsInitializing(true);
    try {
      const accepted = await AuthController.initializePassportSession({ session, passportInstance: instance });
      if (!accepted) return;
      // The sign-in may have started from the logged-out visitor's dialog; it has done its job.
      useAuthStore.getState().setShowSignInDialog(false);
      const completed = useOnboardingStore.getState().experienceCompletedByPubky[publicKey];
      router.replace(completed ? APP_ROUTES.HOME : ONBOARDING_ROUTES.TAGS);
    } catch (error) {
      toast({
        variant: 'error',
        description: isWrongEnvironmentHomeserverError(error)
          ? 'This account belongs to a different homeserver. Use an account for this environment.'
          : isAppError(error)
            ? error.message
            : 'Could not finish logging in. Try again.',
      });
    } finally {
      setIsInitializing(false);
    }
  };
  return { acceptSession, isInitializing };
}
